// src/Pages/Commerce/CreateProductLinkDialog.js
//
// Dialog for creating a new Blitz Commerce product + checkout link.
// Two-column: the form on the left, a live preview of the actual hosted
// checkout page (ProductCheckoutPage.js) on the right, so the merchant
// sees exactly what a buyer will see before they ever publish the link.
//
// On submit, the backend is expected to return a stable checkout_url
// (e.g. https://app.blitz.../checkout/{slug}) that gets embedded as a
// redirect target from the merchant's WooCommerce "Buy" button. That page
// mints a fresh single-use T2A pay-request per buyer — see
// ProductCheckoutPage.js — rather than reusing one payment link.
//
// After a successful create, the dialog swaps to a success screen with
// the generated link front and center (copy button, open in new tab) so
// the merchant doesn't have to hunt for it back in the products table.

import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  InputAdornment,
  Alert,
  Typography,
  Grid,
  Avatar,
  Stack,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { COMMERCE_BRAND, ImageSlot } from "./commerceShared.js";

const initialForm = {
  name: "",
  price: "",
  description: "",
  image_url: "",
  secondary_image_urls: [],
  return_url: "",
};

const MAX_SECONDARY_IMAGES = 4;
const DESCRIPTION_LIMIT = 240;

const fadeUp = keyframes`from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}`;

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

/*------------------------------------------------------------------
  Live checkout preview — condensed version of ProductCheckoutPage's
  hero card, using the same brand + layout so what the merchant sees
  here matches what a buyer will actually see.
-------------------------------------------------------------------*/
function CheckoutPreview({ form, companyName, companyLogoUrl }) {
  const price = parseFloat(form.price) || 0;
  const gallery = [form.image_url, ...form.secondary_image_urls].filter(Boolean);

  return (
    <Box
      sx={{
        borderRadius: 3,
        p: 2.5,
        background: `linear-gradient(160deg, ${COMMERCE_BRAND.skyDeep} 0%, ${COMMERCE_BRAND.sky} 55%, ${alpha(
          COMMERCE_BRAND.skyLight,
          0.9,
        )} 100%)`,
        position: "sticky",
        top: 0,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "#fff", opacity: 0.85, fontWeight: 700, display: "block", mb: 1.5 }}
      >
        Live preview — what buyers will see
      </Typography>

      <Box sx={{ borderRadius: 3, bgcolor: "background.paper", p: 2.5, animation: `${fadeUp} 0.2s ease` }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 2 }}>
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: 1,
              bgcolor: alpha(COMMERCE_BRAND.sky, 0.12),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {companyLogoUrl ? (
              <Box component="img" src={companyLogoUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <StorefrontOutlinedIcon sx={{ fontSize: 13, color: COMMERCE_BRAND.skyDark }} />
            )}
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: "0.02em" }}>
            {companyName || "Blitz Commerce"}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.5 }}>
          <Avatar
            variant="rounded"
            src={form.image_url || undefined}
            sx={{ width: 44, height: 44, bgcolor: alpha(COMMERCE_BRAND.sky, 0.12), color: COMMERCE_BRAND.skyDark }}
          >
            <StorefrontOutlinedIcon fontSize="small" />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={800} noWrap>
              {form.name || "Your product name"}
            </Typography>
            {form.description ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
              >
                {form.description}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.disabled">
                A short description of what buyers are paying for.
              </Typography>
            )}
          </Box>
        </Stack>

        {gallery.length > 1 && (
          <Stack direction="row" spacing={0.75} sx={{ mb: 1.5 }}>
            {gallery.map((url, i) => (
              <Box
                key={url + i}
                component="img"
                src={url}
                alt=""
                sx={{ width: 26, height: 26, borderRadius: 1, objectFit: "cover", border: i === 0 ? `2px solid ${COMMERCE_BRAND.sky}` : "none" }}
              />
            ))}
          </Stack>
        )}

        <Typography
          variant="h5"
          sx={{ fontWeight: 900, letterSpacing: "-0.02em", color: COMMERCE_BRAND.ink, mb: 2 }}
        >
          {fmt(price)}
        </Typography>

        <Divider sx={{ mb: 1.5 }} />

        <TextField
          size="small"
          label="Name"
          fullWidth
          disabled
          placeholder="Jane Doe"
          sx={{ mb: 1, "& .MuiInputBase-input": { fontSize: "0.8rem" } }}
        />
        <TextField
          size="small"
          label="Email"
          fullWidth
          disabled
          placeholder="buyer@example.com"
          sx={{ mb: 1.25, "& .MuiInputBase-input": { fontSize: "0.8rem" } }}
        />
        <Button variant="contained" fullWidth disabled size="small" sx={{ textTransform: "none" }}>
          Pay {fmt(price)}
        </Button>
      </Box>

      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mt: 1.5 }}>
        <LockOutlinedIcon sx={{ fontSize: 11, color: "#fff", opacity: 0.85 }} />
        <Typography variant="caption" sx={{ color: "#fff", fontSize: 10, opacity: 0.85 }}>
          Secured by Blitz · Stripe · PayPal
        </Typography>
      </Stack>
    </Box>
  );
}

/*------------------------------------------------------------------
  Success screen — shown once createdProduct comes back from the
  mutation, so the checkout link is immediately front-and-center
  instead of merchants having to go find it in the table.
-------------------------------------------------------------------*/
function SuccessView({ createdProduct, onCreateAnother, onDone }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(createdProduct.checkout_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box sx={{ textAlign: "center", py: 2, animation: `${fadeUp} 0.2s ease` }}>
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          bgcolor: alpha("#16A34A", 0.12),
          color: "#16A34A",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 2,
        }}
      >
        <CheckCircleOutlinedIcon sx={{ fontSize: 36 }} />
      </Box>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
        {createdProduct.name} is live
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Drop this link into your WooCommerce "Buy" button, socials, or an
        invoice — it works for unlimited buyers.
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1.5,
          borderRadius: 2,
          bgcolor: (theme) => alpha(theme.palette.action.hover, 0.6),
          border: "1px solid",
          borderColor: "divider",
          maxWidth: 480,
          mx: "auto",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            fontFamily: "monospace",
            fontSize: "0.85rem",
            textAlign: "left",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {createdProduct.checkout_url}
        </Typography>
        <Tooltip title={copied ? "Copied!" : "Copy link"}>
          <IconButton size="small" onClick={handleCopy} color={copied ? "success" : "default"}>
            <ContentCopyOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Open checkout page">
          <IconButton
            size="small"
            component="a"
            href={createdProduct.checkout_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <OpenInNewOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<AddOutlinedIcon />}
          onClick={onCreateAnother}
          sx={{ textTransform: "none" }}
        >
          Create another
        </Button>
        <Button
          variant="contained"
          onClick={onDone}
          sx={{
            textTransform: "none",
            bgcolor: COMMERCE_BRAND.sky,
            "&:hover": { bgcolor: COMMERCE_BRAND.skyDark },
          }}
        >
          Done
        </Button>
      </Stack>
    </Box>
  );
}

export default function CreateProductLinkDialog({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  error,
  createdProduct,
  onCreateAnother,
  companyName,
  companyLogoUrl,
}) {
  const [form, setForm] = useState(initialForm);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleClose = () => {
    setForm(initialForm);
    onClose();
  };

  const setSecondaryImage = (index, url) =>
    setForm((prev) => {
      const next = [...prev.secondary_image_urls];
      next[index] = url;
      return { ...prev, secondary_image_urls: next };
    });

  const removeSecondaryImage = (index) =>
    setForm((prev) => ({
      ...prev,
      secondary_image_urls: prev.secondary_image_urls.filter((_, i) => i !== index),
    }));

  const handleSubmit = () => {
    if (!form.name.trim() || !form.price) return;
    onSubmit({
      name: form.name.trim(),
      price: parseFloat(form.price),
      description: form.description.trim(),
      image_url: form.image_url || null,
      gallery_image_urls: form.secondary_image_urls.filter(Boolean),
      return_url: form.return_url.trim() || null,
    });
  };

  const handleCreateAnother = () => {
    setForm(initialForm);
    onCreateAnother?.();
  };

  const isValid = form.name.trim().length > 0 && parseFloat(form.price) > 0;
  const showSuccess = !!createdProduct;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={showSuccess ? "sm" : "md"} fullWidth>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, pt: 2.5, pb: showSuccess ? 0 : 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {showSuccess ? "Link created" : "Create a product link"}
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent dividers={!showSuccess}>
        {showSuccess ? (
          <SuccessView
            createdProduct={createdProduct}
            onCreateAnother={handleCreateAnother}
            onDone={handleClose}
          />
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error?.message || "Something went wrong creating this product."}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This creates a hosted checkout link you can drop into your
                  WooCommerce "Buy" button. Payments land directly in your
                  Blitz Balance.
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Product name"
                    placeholder="Cookie Dozen"
                    fullWidth
                    value={form.name}
                    onChange={handleChange("name")}
                    autoFocus
                  />
                  <TextField
                    label="Price"
                    type="number"
                    fullWidth
                    value={form.price}
                    onChange={handleChange("price")}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label="Description"
                    placeholder="A dozen fresh-baked cookies, shipped same day."
                    fullWidth
                    multiline
                    minRows={3}
                    value={form.description}
                    onChange={(e) => {
                      if (e.target.value.length <= DESCRIPTION_LIMIT) handleChange("description")(e);
                    }}
                    helperText={`${form.description.length}/${DESCRIPTION_LIMIT}`}
                    FormHelperTextProps={{ sx: { textAlign: "right", mr: 0 } }}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                      Photos
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                      <ImageSlot
                        url={form.image_url}
                        onUploaded={(url) => setForm((prev) => ({ ...prev, image_url: url }))}
                        onRemove={() => setForm((prev) => ({ ...prev, image_url: "" }))}
                        size={88}
                        label="Primary"
                      />
                      {form.secondary_image_urls.map((url, i) => (
                        <ImageSlot
                          key={i}
                          url={url}
                          onUploaded={(uploadedUrl) => setSecondaryImage(i, uploadedUrl)}
                          onRemove={() => removeSecondaryImage(i)}
                          size={56}
                        />
                      ))}
                      {form.secondary_image_urls.length < MAX_SECONDARY_IMAGES && (
                        <ImageSlot
                          url={null}
                          onUploaded={(url) =>
                            setForm((prev) => ({
                              ...prev,
                              secondary_image_urls: [...prev.secondary_image_urls, url],
                            }))
                          }
                          onRemove={() => {}}
                          size={56}
                          label="Add"
                        />
                      )}
                    </Stack>
                  </Box>
                  <TextField
                    label="Return URL (optional)"
                    placeholder="https://your-store.com/thank-you"
                    fullWidth
                    value={form.return_url}
                    onChange={handleChange("return_url")}
                    helperText="Send buyers back to your site after they pay. Leave blank to show a generic thank-you screen instead."
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={5} sx={{ display: { xs: "none", md: "block" } }}>
                <CheckoutPreview form={form} companyName={companyName} companyLogoUrl={companyLogoUrl} />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      {!showSuccess && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            sx={{
              textTransform: "none",
              bgcolor: COMMERCE_BRAND.sky,
              "&:hover": { bgcolor: COMMERCE_BRAND.skyDark },
            }}
          >
            {isSubmitting ? "Creating..." : "Create link"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
